// Puerto verbatim de packages/hce/src/hce/consentimiento/bloqueo.ts.
//
// H-002: esta guarda es solo un AVISO antes de enviar (UX). La que manda es la del servidor (packages/web/src/api/lib/hce-guardas-escritura.ts, en el proxy a
// Medplum), que evalúa el recurso ALMACENADO y cubre más tipos. Cambiar una regla aquí sin cambiarla allá no protege nada.
import type { MedplumClient } from '@medplum/core';
import type { Device, Organization, Patient, Practitioner, PractitionerRole, Provenance, Reference, RelatedPerson } from '@medplum/fhirtypes';

type AutorAdenda = Reference<Practitioner | PractitionerRole | RelatedPerson | Patient | Device | Organization>;

const EXT_PLAZO_BLOQUEO = 'http://logiacare.ec/fhir/plazo-bloqueo-horas';
const PLAZO_BLOQUEO_DEFAULT_HORAS = 48;

let plazoBloqueoCache: number | undefined;

async function obtenerPlazoBloqueoHoras(medplum: MedplumClient): Promise<number> {
  if (plazoBloqueoCache !== undefined) return plazoBloqueoCache;
  try {
    const bundle = await medplum.search('Organization', {});
    const org = bundle.entry?.[0]?.resource;
    const ext = org?.extension?.find((e) => e.url === EXT_PLAZO_BLOQUEO);
    plazoBloqueoCache = typeof ext?.valueInteger === 'number' ? ext.valueInteger : PLAZO_BLOQUEO_DEFAULT_HORAS;
  } catch {
    plazoBloqueoCache = PLAZO_BLOQUEO_DEFAULT_HORAS;
  }
  return plazoBloqueoCache;
}

export function _resetPlazoBloqueoCacheForTests(): void {
  plazoBloqueoCache = undefined;
}

export async function estaBloqueado(medplum: MedplumClient | undefined, resource: { resourceType: string; authored?: string }): Promise<boolean> {
  if (resource.resourceType === 'Consent' || resource.resourceType === 'MedicationRequest') {
    return true;
  }
  if (resource.resourceType !== 'QuestionnaireResponse' || !resource.authored) {
    return false;
  }
  const antiguedadHoras = (Date.now() - Date.parse(resource.authored)) / 3_600_000;
  if (Number.isNaN(antiguedadHoras)) return false;
  const plazo = medplum ? await obtenerPlazoBloqueoHoras(medplum) : PLAZO_BLOQUEO_DEFAULT_HORAS;
  return antiguedadHoras > plazo;
}

export const EXT_ADENDA_TEXTO = 'http://logiacare.ec/fhir/extension/adenda-texto';
export const MOTIVO_ADENDA_MIN = 10;
/** Marca propia de la adenda: la consulta del servidor filtra por ella (token `activity`), sin filtrar en el cliente. */
export const SISTEMA_ACTIVIDAD_ADENDA = 'http://logiacare.ec/fhir/CodeSystem/provenance-actividad';
export const CODIGO_ACTIVIDAD_ADENDA = 'adenda';

export interface CrearAdendaInput {
  target: Reference;
  motivo: string;
  autor: AutorAdenda;
  /** Texto de la adenda (qué se agrega o rectifica). El original NO se toca: esto vive solo en la Provenance. */
  texto?: string;
  /** DocumentReference de la adenda ya firmada por el servidor (PAdES). Se enlaza como `entity` de la Provenance; el original NO se toca. */
  documentoFirmado?: Reference;
}

/** Arma la Provenance de la adenda (sin enviarla): la usan `crearAdenda` y la transacción de `AdendaDialog`, que la crea junto con el DocumentReference firmado. */
export function construirProvenanceAdenda(input: CrearAdendaInput): Provenance {
  return {
    resourceType: 'Provenance',
    target: [input.target],
    recorded: new Date().toISOString(),
    agent: [{ who: input.autor }],
    reason: [{ text: input.motivo }],
    activity: { text: 'correction', coding: [{ system: SISTEMA_ACTIVIDAD_ADENDA, code: CODIGO_ACTIVIDAD_ADENDA }] },
    ...(input.documentoFirmado ? { entity: [{ role: 'source' as const, what: input.documentoFirmado }] } : {}),
    ...(input.texto ? { extension: [{ url: EXT_ADENDA_TEXTO, valueString: input.texto }] } : {}),
  };
}

export async function crearAdenda(medplum: MedplumClient, input: CrearAdendaInput): Promise<Provenance> {
  return medplum.createResource<Provenance>(construirProvenanceAdenda(input));
}

const PAGINAS_MAX = 5;
const TAMANO_PAGINA = 100;
const REFS_POR_CONSULTA = 40;

export interface ResultadoAdendas {
  /** Clave: referencia del original (`DocumentReference/<id>`); cada lista va de la adenda más reciente a la más antigua. */
  readonly porDocumento: Record<string, Provenance[]>;
  /** true si se alcanzó el tope de páginas y pueden faltar adendas. */
  readonly truncada: boolean;
}

/** UNA consulta por lote (no una por documento): adendas de todas las referencias dadas, filtradas por la marca propia y ordenadas en el servidor, paginando con _offset. */
export async function buscarAdendasDe(medplum: MedplumClient, targets: readonly string[]): Promise<ResultadoAdendas> {
  const porDocumento: Record<string, Provenance[]> = {};
  let truncada = false;
  for (let i = 0; i < targets.length; i += REFS_POR_CONSULTA) {
    const lote = targets.slice(i, i + REFS_POR_CONSULTA);
    // Paginación por _offset contra el proxy (ruta relativa); el `link next` es solo una BANDERA: su URL es absoluta y apunta al host de Medplum,
    // saltándose el proxy (401 y cierre de sesión), así que nunca se sigue.
    for (let pagina = 0; ; pagina++) {
      const bundle = await medplum.search('Provenance', {
        target: lote.join(','),
        activity: `${SISTEMA_ACTIVIDAD_ADENDA}|${CODIGO_ACTIVIDAD_ADENDA}`,
        _sort: '-recorded',
        _count: TAMANO_PAGINA,
        _offset: pagina * TAMANO_PAGINA,
      });
      for (const e of bundle.entry ?? []) {
        const p = e.resource;
        if (p?.resourceType !== 'Provenance') continue;
        for (const t of p.target ?? []) {
          if (t.reference && lote.includes(t.reference)) (porDocumento[t.reference] ??= []).push(p);
        }
      }
      if (!bundle.link?.some((l) => l.relation === 'next')) break;
      if (pagina + 1 >= PAGINAS_MAX) {
        truncada = true;
        break;
      }
    }
  }
  return { porDocumento, truncada };
}
